// ABOUTME: React Query orchestration hook for truck settings entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useTruckSettingsConfig } from './useTruckSettingsConfig'
import { useCreateTruckSettings, useCreateTruckSettingsWithScope, useUpdateTruckSettings, useDeleteTruckSettings } from './useTruckSettingsMutations'
import { TruckSettingsConfig, TruckSettingsFormValues } from '../types/truckSettings'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseTruckSettingsEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: TruckSettingsFormValues) => void
}

interface UseTruckSettingsEntityResult {
  config: TruckSettingsConfig | undefined
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

/** Strip _rowId (UI-only) from trucks before equality comparison */
const stripRowIds = (values: TruckSettingsFormValues): TruckSettingsFormValues => ({
  ...values,
  availableTrucks: values.availableTrucks
    ? {
      ...values.availableTrucks,
      trucks: values.availableTrucks.trucks.map(({ _rowId: _, ...rest }) => rest),
    }
    : null,
})

const equalsParent = (values: TruckSettingsFormValues, config: TruckSettingsConfig): boolean =>
  JSON.stringify(stripRowIds(values)) === JSON.stringify(stripRowIds(config.parentValues))

export const useTruckSettingsEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseTruckSettingsEntityParams): UseTruckSettingsEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, isFetching, error } = useTruckSettingsConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    defaultTruckSize: cfg.defaultTruckSize,
    availableTrucks: cfg.availableTrucks,
    truckSizeUomOption: cfg.truckSizeUomOption,
    isTruckTypeSelectable: cfg.isTruckTypeSelectable,
  })), [rawConfig, pendingScope])

  const createMutation = useCreateTruckSettings(countryId, businessLine)
  const createWithScopeMutation = useCreateTruckSettingsWithScope(countryId, businessLine)
  const updateMutation = useUpdateTruckSettings(countryId, businessLine)
  const deleteMutation = useDeleteTruckSettings(countryId, businessLine)

  const formValuesRef = useRef<TruckSettingsFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<TruckSettingsConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: TruckSettingsFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const currentConfig = configRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, currentConfig)) {
          onSuccess({})
          return
        }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, currentConfig)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    isFetching,
    error,
    isSaving,
    actions,
  }
}
